import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_OPERATOR_AIRPORT_DATA,
	URL_CMC_OPERATOR_CAPACITY_DATA,
	URL_CMC_OPERATOR_CONTACTS_DATA,
	URL_CMC_OPERATOR_IT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productPageProps } from "../../common/types/product";

export default function OperatorScheduleData({
	packageName,
	subscribeLink,
	testimonials,
}: productPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Gain access to 115,000+ schedule records, full airport timetables and terminal details, covering 98% of global operator traffic, including cargo carriers."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Over 115,000 schedule records tracked worldwide in collaboration with OAG",
							"Complete airport timetables and terminal/operator details in a user-friendly format",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Specific Operator Timetable",
						description:
							"Get detailed schedule information in a timetable and data layout view based on the specific operator:",
						list: [
							"Origin & destination",
							"Flight number",
							"Days of the week on which they fly",
							"Departure & arrival time",
							"Aircraft IATA code",
							"Duration of the flight",
							"Flight stops, if any",
							"Carrier",
							"Remarks (if another carrier operates the flight)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-schedule-data/schedule-search-by-operator.jpg",
						},
					},
					{
						title: "Specific Route",
						description: (
							<>
								<p>
									View all available flights in a timetable
									and data layout view between selected origin
									and destination, within a specific time
									range or on a particular date.
								</p>
								<p>Get information about the flight:</p>
							</>
						),
						list: [
							"Origin & destination",
							"Flight number",
							"Days of the week on which they fly",
							"Departure & arrival time",
							"Terminal number",
							"Aircraft IATA code",
							"Duration of the flight",
							"Flight stops, if any",
							"Carrier",
							"Remarks",
							"Validity (time frame during which the schedule is valid)",
						],
						visual: {
							url: "/static/img/product/commercial-operator-schedule-data/schedule-search-by-route.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for schedule timetables by origin/destination, aircraft, and operator, using filters for service type, flight type, departure period, and flight options."
				slides={[
					{
						title: "Origin",
						description:
							"Search for flight schedule or route by origin location:",
						list: [
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
							"Origin airport name",
						],
						visual: {
							url: "/static/img/product/commercial-operator-schedule-data/schedule-search-by-origin.jpg",
							title: "Complete list of all scheduled departures from Vienna International Airport",
						},
					},
					{
						title: "Destination",
						description:
							"Search for flight schedule or route by destination location:",
						list: [
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
							"Destination airport name",
						],
						visual: {
							url: "/static/img/product/commercial-operator-schedule-data/schedule-search-by-destination.jpg",
							title: "Complete list of all scheduled arrivals in France",
						},
					},
					{
						title: "Aircraft",
						description:
							"Search for flight schedule or route based on the:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft family",
							"Aircraft variant",
							"IATA code",
						],
						visual: {
							url: "/static/img/product/commercial-operator-schedule-data/schedule-search-by-aircraft.jpg",
							title: "All scheduled flights using widebody Airbus aircraft",
						},
					},
					{
						title: "Settings",
						description:
							"Search for flight schedule or route by various settings:",
						list: [
							"Operator name",
							"Operator's alliance",
							"Service type (cargo, charter, etc.)",
							"Flight type (domestic or international)",
							"Departure date/departure period/earliest departure",
							"Include/exclude options for: return flights, non-stop only flights, codeshares, and wet-leases",
						],
						visual: {
							url: "/static/img/product/commercial-operator-schedule-data/schedule-search-by-settings.jpg",
							title: "List of International KLM flights including codeshares",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Get access to more than 14,000 commercial operator and airport profiles, as well as start-up operators worldwide.",
						url: URL_CMC_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Operator Capacity Data",
						content:
							"Get access to detailed capacity, frequency, and market share information for operators and airports worldwide.",
						url: URL_CMC_OPERATOR_CAPACITY_DATA,
					},
					{
						title: "Operator Contacts Data",
						content:
							"Get a detailed contacts overview for almost any active as well as start-up operators worldwide.",
						url: URL_CMC_OPERATOR_CONTACTS_DATA,
					},
					{
						title: "Operator IT Data",
						content:
							"Get access to a Passenger Service System (PSS) providers for all scheduled airlines as well as each PSS's customer list.",
						url: URL_CMC_OPERATOR_IT_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology exportOptionsAvailable={false} />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Over 115,000 schedule records tracked worldwide in collaboration with OAG",
					"Complete airport timetables and terminal/operator details in a user-friendly format",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Commercial Aviation Operator Schedule Data is not available for download."
				downloadsAvailable={false}
				dataFeedNotAvailableDisclaimer="Operator Schedule Data is not available in a data feed form."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download these data sets?",
						content:
							"The Commercial Aviation Operator Schedule Data cannot be downloaded.",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								The Commercial Aviation Operator Schedule Data
								is not available for download. To learn more
								about which data feeds are available for
								integration, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "How up-to-date are the ch-aviation schedules?",
						content:
							"ch-aviation has partnered with OAG Aviation Worldwide Limited, one of the leading operator schedule data providers, to be able to offer this service for you. We receive weekly schedule updates for the vast majority of all scheduled passenger operators, and also some charter and cargo carriers. While ch-aviation is undertaking a large amount of effort to correct and improve the data quality received from operators by applying a wide range of data integrity checks, we still rely on what is initially filed and provided by the operator and cannot guarantee that the schedules are 100% accurate at any given time.",
					},
					{
						title: "Are the schedules displayed in local time?",
						content:
							"Yes, all departure and arrival times are displayed in local time using the 24-hour clock. If a flight arrives on a different (later or earlier day) than the day of departure, this will be indicated next to the arrival time (+1 = arrival 1 day later, +2 = arrival 2 days later, -1 = arrival 1 day earlier).",
					},
				]}
			/>
		</>
	);
}
